'use client';

/**
 * **추적을 거는 한 길** — 브랜드 키워드 · 자사 계정 · 수집 세트(새로 세우기 / 있는 세트에 더하기).
 * 추적 등록 창 · 추적 관리 · 온보딩이 이 훅 하나를 쓴다 — 길이 여럿이면 칸 검사가 샌다(A662).
 *
 * ```
 *   register(request)      창·온보딩이 쓰는 한 번에 걸기 — 갈래를 보고 아래 낱개로 보낸다
 *   addBrandKeyword …      추적 관리가 쓰는 낱개 — 지금 목록을 함께 받는다
 * ```
 *
 * 🚨 **치환 op 다** — `workspace.brandKeywords.update` · `workspace.brandAccounts.update` ·
 *   세트 갱신(`updateCampaignById`)이 배열을 통째로 갈아 끼운다. 그래서 **지금 목록에 이어 붙여**
 *   보낸다 — 하나만 보내면 나머지가 지워진다.
 * 🚨 **칸 검사는 여기 한 곳이다** — 서버는 플랜을 안 본다(A662). 막히면 업그레이드 안내가 이미 떴고
 *   결과는 `{ ok: false, message: '' }` 다(부르는 쪽은 아무 말도 더하지 않는다).
 * ⚠️ 계정 치환은 기존 계정의 **인스타 세션·unipile 값을 서버가 병합해 지킨다** — `{platform, username}` 만 보낸다.
 * ⚠️ 세트는 **이름과 걸 것(+알림)만** 받는다 — 기간·채널은 만든 뒤 그 화면에서 정한다.
 * 🔑 성공하면 활동 기록(`track_registered`)을 남기고 「수집 중」 표가 읽는 조회 둘을 무효로 한다
 *   (`useTrackedKeywords` · `useAnalyticsCampaigns`).
 */

import type {
  TrackAlerts,
  TrackSlotKind,
  TrackRegistered,
  TrackRegisterType,
  TrackAccountOwner,
} from 'src/utils/workspace/track-register';

import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { log } from 'src/utils/logger';
import { toBrandMentionAlertPatch } from 'src/utils/workspace/track-register';

import { track } from 'src/lib/analytics/client';
import { SET } from 'src/constants/track-set-name';
import { fsCall } from 'src/actions/firestore/dispatch';
import { IPlanService } from 'src/constants/plan-service';
import { usePlanUpgrade } from 'src/contexts/plan-upgrade-context';
import { updateCampaignById, createCampaignsForWorkspace } from 'src/actions/firestore/api/keyword-campaign-api';

import { useKeywordSlotGuard } from './use-keyword-slot-guard';

/** 결과 — 실패면 `message` 가 부르는 쪽이 적을 말이다(빈 글자면 업그레이드 안내가 이미 떴다) */
export type TrackRegisterResult = { ok: boolean; message?: string };

/** 활동 기록의 길 — 어느 자리에서 걸었나 */
export type TrackRegisterSource = 'dialog' | 'status' | 'onboarding';

/** 세트 계정 한 짝 — 서버가 읽는 칸 이름은 `account` 다 */
export type TrackAccountPair = { platform: string; account: string };

/** 브랜드 멘션 알림의 지금 값(`messageSettings.brandMention`) */
export type BrandMentionAlertSettings = {
  messageEmail?: unknown;
  messageEmailWeekly?: unknown;
  messageSlackRealTime?: unknown;
} | null | undefined;

/**
 * 한 번에 걸기 — 창·온보딩이 넘기는 것.
 * 🚨 `current` 는 **지금 걸려 있는 것**이다 — 치환 op 라 함께 보낸다(구독본을 넘겨라).
 */
export type TrackRegisterRequest = {
  type: TrackRegisterType;
  /** 계정 갈래의 길 — 없으면 남의 계정 */
  owner?: TrackAccountOwner;
  /** 말 — 브랜드 키워드 · 키워드 */
  value?: string;
  platform?: string;
  username?: string;
  /** 세트에 거는 갈래(키워드 · 남의 계정)만 — 새 세트 이름 또는 있는 세트 */
  group?: { mode: 'new'; name: string } | { mode: 'existing'; campaignId: string };
  /** 알림 — 브랜드 키워드는 워크스페이스 알림을 고치고, 새 세트는 그 세트 문서에 싣는다 */
  alerts?: TrackAlerts;
  current: {
    brandKeywords?: string[];
    brandAccounts?: Record<string, any>[];
    /** 있는 세트에 더할 때 그 세트가 지금 건 것 */
    campaign?: { keywords: string[]; accounts: TrackAccountPair[] };
    brandMention?: BrandMentionAlertSettings;
    /** 워크스페이스에 Slack 이 연결돼 있나 — 아니면 Slack 칸은 켜지지 않는다 */
    slackConnected?: boolean;
  };
};

/**
 * @param workspaceId 지금 워크스페이스
 * @param options `source` — 활동 기록에 남는 길(기본 `dialog`)
 */
export function useTrackRegister(workspaceId: string, options?: { source?: TrackRegisterSource }) {
  const source = options?.source ?? 'dialog';
  const [saving, setSaving] = useState<TrackSlotKind | null>(null);
  const { guardAdd } = useKeywordSlotGuard();
  const { checkAndShow } = usePlanUpgrade();
  const queryClient = useQueryClient();

  /** 성공 뒤 — 활동 기록 + 「수집 중」 표의 조회 무효화 */
  const settle = useCallback(
    (target: 'brand' | 'keyword' | 'account', count: number) => {
      track({ name: 'track_registered', type: 'action', props: { target, source, count } });
      queryClient.invalidateQueries({ queryKey: ['workspace', 'brand-keywords', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['analytics', 'campaigns', workspaceId] });
    },
    [queryClient, source, workspaceId],
  );

  /**
   * 브랜드 키워드 하나를 **이어 붙인다**.
   * @param current 지금 브랜드 키워드 — 🚨 치환이라 함께 보낸다
   */
  const addBrandKeyword = useCallback(
    async (current: string[], keyword: string): Promise<TrackRegisterResult> => {
      const value = String(keyword ?? '').trim();
      if (!value) return { ok: false, message: '키워드를 입력해 주세요.' };
      if (current.some((k) => k.toLowerCase() === value.toLowerCase())) {
        return { ok: false, message: '이미 등록된 키워드입니다.' };
      }
      if (checkAndShow(IPlanService.brand.brandKeywordCount, current.length + 1)) return { ok: false, message: '' };
      setSaving('brandKeyword');
      try {
        const res = await fsCall('workspace.brandKeywords.update', {
          workspaceId,
          brandKeywords: [...current, value],
        });
        if (!res?.ok) return { ok: false, message: '등록하지 못했습니다.' };
        settle('brand', 1);
        return { ok: true };
      } catch (error) {
        log.error(SERVICE_NAME, '브랜드 키워드 등록 실패', { err: (error as Error)?.message });
        return { ok: false, message: '등록하지 못했습니다.' };
      } finally {
        setSaving(null);
      }
    },
    [workspaceId, checkAndShow, settle],
  );

  /**
   * 자사 계정 하나를 **이어 붙인다** — 여기서 건 계정은 「미연동」으로 선다(연동은 자사 계정 관리).
   * @param current 지금 자사 계정 — 🚨 치환이라 함께 보낸다
   */
  const addBrandAccount = useCallback(
    async (current: Record<string, any>[], platform: string, username: string): Promise<TrackRegisterResult> => {
      const handle = String(username ?? '').trim().replace(/^@+/, '');
      if (!platform) return { ok: false, message: '채널을 골라 주세요.' };
      if (!handle) return { ok: false, message: '계정을 입력해 주세요.' };
      const exists = current.some(
        (a) =>
          String(a?.platform ?? '').toLowerCase() === platform.toLowerCase() &&
          String(a?.username ?? '').toLowerCase() === handle.toLowerCase(),
      );
      if (exists) return { ok: false, message: '이미 등록된 계정입니다.' };
      if (checkAndShow(IPlanService.brandAccount.brandAccountCount, current.length + 1)) return { ok: false, message: '' };

      setSaving('brandAccount');
      try {
        const res = await fsCall('workspace.brandAccounts.update', {
          workspaceId,
          brandAccounts: [
            ...current.map((a) => ({ platform: String(a?.platform ?? ''), username: String(a?.username ?? '') })),
            { platform, username: handle },
          ],
        });
        if (!res?.ok) return { ok: false, message: '등록하지 못했습니다.' };
        settle('account', 1);
        return { ok: true };
      } catch (error) {
        log.error(SERVICE_NAME, '자사 계정 등록 실패', { err: (error as Error)?.message });
        return { ok: false, message: '등록하지 못했습니다.' };
      } finally {
        setSaving(null);
      }
    },
    [workspaceId, checkAndShow, settle],
  );

  /**
   * 수집 세트를 **새로 만든다** — 이름 + 걸 것(키워드·계정 중 하나 이상).
   *
   * 🚨 키워드만이 아니다 — 계정만 건 세트는 계정 추적에 선다(`utils/workspace/track-kind`).
   * 🚨 칸은 **키워드와 계정을 함께** 센다.
   * @param alerts 알림 칸 — 안 주면 서버 기본(매일 이메일 켬)
   * @returns 성공이면 `campaignId` — 만든 세트 화면으로 곧장 보낼 때 쓴다(모르면 빈 글자)
   */
  const addTargetCampaign = useCallback(
    async (
      name: string,
      keywords: string[],
      accounts: { platform: string; username?: string; account?: string }[] = [],
      alerts?: TrackAlerts,
    ): Promise<TrackRegisterResult & { campaignId?: string }> => {
      const value = String(name ?? '').trim();
      if (!value) return { ok: false, message: `${SET} 이름을 적어 주세요.` };
      const list = [...new Set(keywords.map((k) => k.trim()).filter(Boolean))];
      const handles = toAccountList(accounts);
      if (list.length === 0 && handles.length === 0) {
        return { ok: false, message: '키워드나 계정을 하나 이상 적어 주세요.' };
      }
      if (guardAdd(list.length + handles.length)) return { ok: false, message: '' };
      setSaving('targetKeyword');
      try {
        const res = await createCampaignsForWorkspace(workspaceId, [{
          name: value,
          keywords: list,
          accounts: handles,
          ...(alerts
            ? { messageEmail: alerts.daily, messageEmailWeekly: alerts.weekly, messageSlackRealTime: alerts.slack }
            : {}),
        }]);
        if (!res.ok) return { ok: false, message: `${SET}를 만들지 못했습니다.` };
        settle(list.length > 0 ? 'keyword' : 'account', list.length + handles.length);
        return { ok: true, campaignId: res.ids?.[0] ?? '' };
      } catch (error) {
        log.error(SERVICE_NAME, '타겟 키워드 캠페인 생성 실패', { err: (error as Error)?.message });
        return { ok: false, message: `${SET}를 만들지 못했습니다.` };
      } finally {
        setSaving(null);
      }
    },
    [workspaceId, guardAdd, settle],
  );

  /**
   * 세트 하나에 키워드를 **이어 붙인다**.
   * @param current 그 세트가 지금 건 키워드 — 🚨 치환이라 함께 보낸다
   */
  const addCampaignKeyword = useCallback(
    async (campaignId: string, current: string[], keyword: string): Promise<TrackRegisterResult> => {
      const value = String(keyword ?? '').trim();
      if (!campaignId) return { ok: false, message: `어느 ${SET}인지 알 수 없습니다.` };
      if (!value) return { ok: false, message: '키워드를 입력해 주세요.' };
      if (current.some((k) => k.toLowerCase() === value.toLowerCase())) {
        return { ok: false, message: `이 ${SET}에 이미 등록된 키워드입니다.` };
      }
      if (guardAdd(1)) return { ok: false, message: '' };
      setSaving('targetKeyword');
      try {
        const res = await updateCampaignById(campaignId, { keywords: [...current, value] });
        if (!res.ok) return { ok: false, message: '등록하지 못했습니다.' };
        settle('keyword', 1);
        return { ok: true };
      } catch (error) {
        log.error(SERVICE_NAME, '캠페인 키워드 등록 실패', { err: (error as Error)?.message });
        return { ok: false, message: '등록하지 못했습니다.' };
      } finally {
        setSaving(null);
      }
    },
    [guardAdd, settle],
  );

  /**
   * 세트 하나에 계정을 **이어 붙인다**.
   * @param current 그 세트가 지금 건 계정 — 🚨 치환이라 함께 보낸다
   */
  const addCampaignAccount = useCallback(
    async (
      campaignId: string,
      current: { platform: string; account: string }[],
      platform: string,
      username: string,
    ): Promise<TrackRegisterResult> => {
      const handle = String(username ?? '').trim().replace(/^@+/, '');
      const channel = String(platform ?? '').trim();
      if (!campaignId) return { ok: false, message: `어느 ${SET}인지 알 수 없습니다.` };
      if (!handle) return { ok: false, message: '계정 아이디를 적어 주세요.' };
      if (!channel) return { ok: false, message: '채널을 골라 주세요.' };
      const kept = toAccountList(current);
      if (kept.some((a) => a.platform === channel && a.account.toLowerCase() === handle.toLowerCase())) {
        return { ok: false, message: '이미 등록된 계정입니다.' };
      }
      if (guardAdd(1)) return { ok: false, message: '' };
      setSaving('targetKeyword');
      try {
        const res = await updateCampaignById(campaignId, {
          accounts: [...kept, { platform: channel, account: handle }],
        });
        if (!res.ok) return { ok: false, message: '계정을 추가하지 못했습니다.' };
        settle('account', 1);
        return { ok: true };
      } catch (error) {
        log.error(SERVICE_NAME, '세트 계정 추가 실패', { err: (error as Error)?.message });
        return { ok: false, message: '계정을 추가하지 못했습니다.' };
      } finally {
        setSaving(null);
      }
    },
    [guardAdd, settle],
  );

  /**
   * 브랜드 멘션 알림 세 칸을 **바뀐 것만** 고친다(`messageSettings.brandMention.*` 점 경로).
   *
   * 🚨 **워크스페이스 한 벌이다** — 브랜드 키워드 전체에 걸린다. 그래서 바뀐 칸만 쓴다.
   * ⚠️ Slack 은 연결돼 있을 때만 켤 수 있다(알림 설정 화면과 같은 규칙).
   * @param write `explicit` — 참이면 이메일 두 칸을 **바뀌지 않았어도 값으로 적는다**(시작하기가
   *   「고른 적 있다」를 남길 때 · 저장값이 없으면 매일 이메일은 켬으로 읽혀 바뀐 것이 없다)
   * @returns 쓸 것이 없으면 쓰지 않고 성공이다
   */
  const saveBrandMentionAlerts = useCallback(
    async (
      current: BrandMentionAlertSettings,
      next: TrackAlerts,
      slackConnected: boolean,
      write?: { explicit?: boolean },
    ): Promise<TrackRegisterResult> => {
      const payload = toBrandMentionAlertPatch(current, next, slackConnected, Boolean(write?.explicit));
      if (Object.keys(payload).length === 0) return { ok: true };
      try {
        const res = await fsCall('workspace.fields.update', { workspaceId, payload });
        return res?.ok === false ? { ok: false, message: '알림 설정을 저장하지 못했습니다.' } : { ok: true };
      } catch (error) {
        log.error(SERVICE_NAME, '브랜드 멘션 알림 저장 실패', { err: (error as Error)?.message });
        return { ok: false, message: '알림 설정을 저장하지 못했습니다.' };
      }
    },
    [workspaceId],
  );

  /**
   * 한 번에 건다 — 갈래를 보고 위의 낱개로 보낸다.
   *
   * ⚠️ 브랜드 키워드의 알림 저장이 실패해도 **등록은 성공**이다 — 걸린 것은 걸렸다. 그때 `message` 에
   *   알림을 못 고쳤다고 적는다.
   * @returns 성공이면 `done` — 가는 곳은 `toTrackDestination(done)` 이 짓는다
   */
  const register = useCallback(
    async (request: TrackRegisterRequest): Promise<TrackRegisterResult & { done?: TrackRegistered }> => {
      const { type, current } = request;
      const owner: TrackAccountOwner = request.owner ?? 'other';
      const value = String(request.value ?? '').trim();
      const handle = String(request.username ?? '').trim().replace(/^@+/, '');
      const platform = String(request.platform ?? '').trim();

      if (type === 'brand') {
        const res = await addBrandKeyword(current.brandKeywords ?? [], value);
        if (!res.ok) return res;
        if (request.alerts) {
          const saved = await saveBrandMentionAlerts(current.brandMention, request.alerts, Boolean(current.slackConnected));
          if (!saved.ok) return { ok: true, message: saved.message, done: { type, owner, value } };
        }
        return { ok: true, done: { type, owner, value, alerts: request.alerts } };
      }

      if (type === 'account' && owner === 'own') {
        const res = await addBrandAccount(current.brandAccounts ?? [], platform, handle);
        return res.ok ? { ok: true, done: { type, owner, value: handle } } : res;
      }

      const group = request.group ?? { mode: 'new' as const, name: type === 'account' ? handle : value };
      if (type === 'keyword' && !value) return { ok: false, message: '키워드를 입력해 주세요.' };
      if (type === 'account' && !handle) return { ok: false, message: '계정 아이디를 적어 주세요.' };

      if (group.mode === 'existing') {
        const campaign = current.campaign ?? { keywords: [], accounts: [] };
        const res = type === 'account'
          ? await addCampaignAccount(group.campaignId, campaign.accounts, platform, handle)
          : await addCampaignKeyword(group.campaignId, campaign.keywords, value);
        if (!res.ok) return res;
        return { ok: true, done: { type, owner, value: type === 'account' ? handle : value, campaignId: group.campaignId } };
      }

      const res = type === 'account'
        ? await addTargetCampaign(group.name, [], [{ platform, username: handle }], request.alerts)
        : await addTargetCampaign(group.name, [value], [], request.alerts);
      if (!res.ok) return res;
      return {
        ok: true,
        done: {
          type,
          owner,
          value: type === 'account' ? handle : value,
          campaignId: res.campaignId,
          alerts: request.alerts,
        },
      };
    },
    [addBrandKeyword, addBrandAccount, addTargetCampaign, addCampaignKeyword, addCampaignAccount, saveBrandMentionAlerts],
  );

  return {
    saving,
    register,
    addBrandKeyword,
    addBrandAccount,
    addTargetCampaign,
    addCampaignKeyword,
    addCampaignAccount,
    saveBrandMentionAlerts,
  };
}

/**
 * 계정 목록을 **서버가 읽는 꼴**로 맞춘다 — `{ platform, account }`.
 *
 * 🚨 칸 이름이 `account` 다(`server/fs/keyword-campaign-crud` 의 `toAccounts`) — `username` 으로 보내면
 *   서버가 빈 값으로 걸러, 아무것도 안 걸린 세트가 만들어진다.
 * ⚠️ `@` 를 떼고 빈 것을 걸러 낸다.
 */
export function toAccountList(
  raw: readonly { platform?: string; account?: string; username?: string }[],
): TrackAccountPair[] {
  return (Array.isArray(raw) ? raw : [])
    .map((a) => ({
      platform: String(a?.platform ?? '').trim(),
      account: String(a?.account ?? a?.username ?? '').trim().replace(/^@+/, ''),
    }))
    .filter((a) => a.platform !== '' && a.account !== '');
}

const SERVICE_NAME = 'common/track-register';
